/**
 * ACT zone — action cards (ScheduleMisalignment / DemandSpike / SetpointCounterfactual).
 *
 * Ported from the pre-`ActionCardSlot` ACT hero (see git history at
 * c8e4460b8:web-ui/src/modules/insights/homepage/action-cards.tsx). Structure
 * + MUI styling carried over; retyped against the current `ActionCardSlot`
 * contract and built only from atoms already in `./atoms` (no net-new
 * `@/nzds/...` components beyond the existing `Button`).
 *
 * Standards: impact uses the shared `useFormatCurrency` hook (locale +
 * user-currency aware, no compact notation — the card leads with the exact
 * window figure). Copy is historical-framed: "observed", "up to" for
 * ceiling ranges. Never "savings"/"projected"/"forecast".
 */
import type React from "react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Page404 } from "@/components/nav/page404"
import { useFormatCurrency } from "@/formatters/use-format-currency"
import { Button } from "@/nzds/inputs/button"

import {
  ArrowForwardRounded,
  PushPinRounded,
  VerifiedRounded,
} from "@mui/icons-material"
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"

import { Card, ConfidenceChip, Eyebrow, SectionTitle } from "./atoms"
import type { ActionCardSlot } from "./insights-homepage.types"

// ── action card ──────────────────────────────────────────────────────────────

export const ActionCard: React.FC<{
  card: ActionCardSlot
  onReview: (id: string) => void
}> = ({ card, onReview }) => {
  const { t } = useTranslation("common")
  // Exact window figure — no compact notation (the card leads with the
  // precise identified-potential-savings $, not a rounded headline number).
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const range = card.impact_dollars_range
  const impactDisplay = range
    ? `${formatCurrency(range[0])}–${formatCurrency(range[1])}`
    : formatCurrency(card.impact_dollars)

  return (
    <Card
      sx={{
        position: "relative",
        // Flex-fill: grow/shrink from a 0 basis so the row never overflows;
        // the top action's larger ratio keeps it wider than the rest.
        flex: card.top_action ? "1.35 1 0" : "1 1 0",
        minWidth: 0,
        minHeight: card.top_action ? 216 : 188,
        height: "100%",
        p: card.top_action ? "18px 22px" : "16px 18px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 1,
      }}
      variant={card.top_action ? "top-action" : "default"}
    >
      {card.top_action ? (
        <Chip
          color="primary"
          icon={<PushPinRounded sx={{ fontSize: 14 }} />}
          label="Top action"
          size="small"
          sx={{
            alignSelf: "flex-start",
            height: 26,
            px: 0.5,
            fontWeight: 700,
            fontSize: "0.75rem",
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: "primary.contrastText",
            bgcolor: "primary.main",
            boxShadow: 2,
            "& .MuiChip-icon": { color: "primary.contrastText", ml: 0.5 },
          }}
        />
      ) : null}

      {/* Top: WHO this is for — the site leads the card as its headline, with
          the detector + confidence as small supporting chips beneath. */}
      <Stack spacing={0.5}>
        <Typography
          component="h2"
          sx={{
            fontWeight: "bold",
            lineHeight: 1.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          variant="h2"
        >
          {card.site_label}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
        >
          {/* Human detector name — never the internal code (E2/E3/C1). On the
              top-action card, force a strong high-contrast fill (primary.dark on
              white text) so the badge reads clearly against the light card. */}
          <Chip
            color={card.top_action ? "primary" : "default"}
            label={card.detector_label}
            size="small"
            sx={{
              letterSpacing: "0.02em",
              fontWeight: 700,
              ...(card.top_action && {
                bgcolor: "primary.dark",
                color: "primary.contrastText",
              }),
            }}
            variant={card.top_action ? "filled" : "outlined"}
          />
          <ConfidenceChip level={card.confidence} />
          {card.caveat_badge ? (
            <Chip
              color="default"
              label={card.caveat_badge}
              size="small"
              sx={{ letterSpacing: "0.05em" }}
              variant="outlined"
            />
          ) : null}
        </Stack>
      </Stack>

      {/* Mid: the impact $ stays the hero, with the action verb beneath it. */}
      <Stack spacing={0.5}>
        <Box sx={{ minWidth: 0 }}>
          {range ? <Eyebrow>up to</Eyebrow> : null}
          <Typography
            color="text.primary"
            sx={{ fontWeight: "bold", lineHeight: 1.05, whiteSpace: "nowrap" }}
            variant={range ? "h1" : "hero"}
          >
            {impactDisplay}
          </Typography>
          <Eyebrow sx={{ mt: 0.25 }}>
            {range ? t("neat.observedCeiling") : t("neat.observed")}
          </Eyebrow>
        </Box>
        <Typography
          color="text.primary"
          sx={{ fontWeight: 500, lineHeight: 1.4, textWrap: "pretty" }}
          variant="body2"
        >
          {card.action_text}
        </Typography>
      </Stack>

      {/* CTA */}
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          endIcon={<ArrowForwardRounded />}
          onClick={() => {
            onReview(card.id)
          }}
          size="small"
          variant="contained"
        >
          {card.cta_label}
        </Button>
      </Stack>
    </Card>
  )
}

// ── all-clear hero (empty state) ─────────────────────────────────────────────

const AllClearHero: React.FC = () => {
  const theme = useTheme()
  return (
    <Card
      sx={{
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        alignItems: "center",
        gap: 2.5,
        p: "20px 24px",
        minHeight: 120,
        bgcolor: theme.palette.success.light,
        borderColor: theme.palette.success.main,
      }}
      variant="success"
    >
      <Box
        sx={{
          height: 56,
          width: 56,
          borderRadius: "50%",
          bgcolor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <VerifiedRounded sx={{ fontSize: 32 }} />
      </Box>
      <Box>
        <SectionTitle>Portfolio is all clear</SectionTitle>
        <Typography
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
          variant="body2"
        >
          Every scanned site passed every detector before standup.
        </Typography>
      </Box>
    </Card>
  )
}

// ── loading hero (feed pending) ──────────────────────────────────────────────
// Hold this while the ACT feed is in-flight — otherwise an empty-until-resolved
// hero flashes "Portfolio is all clear" before the data arrives.
const LoadingHero: React.FC = () => (
  <Card
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: "20px 24px",
      minHeight: 120,
    }}
    variant="demoted"
  >
    <CircularProgress size={20} />
    <Typography color="text.secondary" variant="body2">
      Loading recommended actions…
    </Typography>
  </Card>
)

// ── row (rank + pin + layout) ─────────────────────────────────────────────────

// A horizontal row of flex-fill cards (the ACT "top actions" strip). Cards
// grow/shrink from a 0 basis to fill the row exactly — never overflowing into a
// horizontal scroll — while their flex ratios keep the top action wider than
// the rest and the trailing "explore all" card narrowest (see ActionCard /
// ExploreAllCard).
const CardsGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "stretch",
  gap: theme.spacing(2),
}))

// Above-fold cap: surface the top few actions, then a trailing card that opens
// the full list (the strawman's "3-4 max above fold" rule).
const MAX_VISIBLE_ACTIONS = 3

const ExploreAllCard: React.FC<{ remaining: number; onClick: () => void }> = ({
  remaining,
  onClick,
}) => (
  <Card
    aria-label="Explore all recommended actions"
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        onClick()
      }
    }}
    role="button"
    sx={{
      // Narrowest tile in the row — a compact "see the rest" affordance.
      flex: "0.7 1 0",
      minWidth: 0,
      minHeight: 188,
      height: "100%",
      p: "16px 18px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      textAlign: "center",
      cursor: "pointer",
    }}
    tabIndex={0}
    variant="demoted"
  >
    <ArrowForwardRounded />
    <SectionTitle>Explore all recommended actions</SectionTitle>
    <Typography color="text.secondary" variant="body2">
      +{remaining} more
    </Typography>
  </Card>
)

export const ActionCardRow: React.FC<{
  cards: readonly ActionCardSlot[]
  onReview: (id: string) => void
  onExploreAll: () => void
  loading?: boolean
  error?: boolean
}> = ({ cards, onReview, onExploreAll, loading = false, error = false }) => {
  const ranked = useMemo(
    () =>
      [...cards]
        .sort((a, b) => b.impact_dollars - a.impact_dollars)
        .map((c, i) => ({ ...c, top_action: i === 0 })),
    [cards]
  )

  // Precedence: loading (don't flash all-clear before the feed resolves) ->
  // error (a failed feed must not read as all-clear) -> empty -> cards.
  if (loading) {
    return <LoadingHero />
  }

  if (error) {
    return <Page404 />
  }

  if (ranked.length === 0) {
    return <AllClearHero />
  }

  const visible = ranked.slice(0, MAX_VISIBLE_ACTIONS)
  const remaining = ranked.length - visible.length

  return (
    <CardsGrid>
      {visible.map((c) => (
        <ActionCard key={c.id} card={c} onReview={onReview} />
      ))}
      {remaining > 0 ? (
        <ExploreAllCard onClick={onExploreAll} remaining={remaining} />
      ) : null}
    </CardsGrid>
  )
}
